import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Video, Globe, ShoppingBag, Mail, Bot, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TranslatedText } from '@/components/TranslatedText';

const academies = [
  {
    icon: Video,
    number: "01",
    title: "Faceless Content",
    description: "Master AI-powered content creation for TikTok, YouTube, Instagram, OnlyFans and beyond — build audiences and monetize without showing your face.",
    color: "blue",
  },
  {
    icon: Globe,
    number: "02",
    title: "Passive Traffic Sites",
    description: "Create websites designed to generate organic traffic on autopilot. Traffic becomes leads. Leads become revenue.",
    color: "purple",
  },
  {
    icon: ShoppingBag,
    number: "03",
    title: "E-Commerce, Shopify & Print-on-Demand",
    description: "Launch profitable online stores that sell for you — without inventory, packaging, or handling products.",
    color: "green",
  },
  {
    icon: Mail,
    number: "04",
    title: "Newsletter Empires",
    description: "Build valuable email audiences and monetize them using AI-assisted content systems.",
    color: "orange",
  },
  {
    icon: Bot,
    number: "05",
    title: "AI & Automation",
    description: "Scale faster using AI tools and smart automations that solve real problems for people and businesses.",
    color: "pink",
  },
  {
    icon: ArrowRight,
    number: "06+",
    title: "More Coming Soon",
    description: "New academies and business models are added regularly. Next release: Clawbot Full Course.",
    color: "slate",
  },
];

const colorClasses = {
  blue: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600" },
  purple: { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600" },
  green: { bg: "bg-green-50", border: "border-green-200", icon: "text-green-600" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", icon: "text-orange-600" },
  pink: { bg: "bg-pink-50", border: "border-pink-200", icon: "text-pink-600" },
  slate: { bg: "bg-slate-100", border: "border-slate-300 border-dashed", icon: "text-slate-500" },
};

export const LightSolutionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section ref={ref} id="solution" className="py-12 sm:py-20 px-4 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3 px-2">
            <TranslatedText>A University-Level Digital Academy</TranslatedText>
          </h2>
          <p className="text-base text-slate-600 max-w-xl mx-auto px-2 leading-relaxed">
            <TranslatedText>5 full academies, each with 10 in-depth modules. Every module is a complete course — 10 video classes, detailed written lessons, and 10 hands-on exercises.</TranslatedText>
          </p>
        </motion.div>

        {/* Stats Row - Horizontal scroll on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex justify-center gap-6 sm:gap-10 mb-10 p-4 bg-slate-900 rounded-2xl text-white"
        >
          {[
            { number: "500+", label: "Lessons" },
            { number: "50", label: "Modules" },
            { number: "5", label: "Academies" },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <p className="text-2xl sm:text-3xl font-bold">{stat.number}</p>
              <p className="text-xs sm:text-sm text-slate-400">
                <TranslatedText>{stat.label}</TranslatedText>
              </p>
            </div>
          ))}
        </motion.div>

        {/* Academies - Compact cards for mobile */}
        <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 mb-10">
          {academies.map((academy, index) => {
            const colors = colorClasses[academy.color as keyof typeof colorClasses];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.15 + index * 0.05 }}
                className={`p-4 sm:p-5 rounded-xl ${colors.bg} border ${colors.border} flex items-start gap-4`}
              >
                <div className={`w-11 h-11 rounded-lg bg-white flex items-center justify-center shadow-sm flex-shrink-0`}>
                  <academy.icon className={`w-5 h-5 ${colors.icon}`} />
                </div>
                <div className="min-w-0">
                  <span className={`text-xs font-semibold ${colors.icon}`}>{academy.number} —</span>
                  <h3 className="text-base font-semibold text-slate-900 mt-0.5">
                    <TranslatedText>{academy.title}</TranslatedText>
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mt-1">
                    <TranslatedText>{academy.description}</TranslatedText>
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="text-center"
        >
          <Button
            onClick={scrollToPricing}
            size="lg"
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white text-base font-semibold h-14 px-10 rounded-xl shadow-lg shadow-blue-600/25"
          >
            <TranslatedText>Get Full Access</TranslatedText>
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default LightSolutionSection;
